import { useIonRouter } from '@ionic/vue';
import { useRoute } from 'vue-router';

export function useRouting() {
  const router = useIonRouter();
  const route = useRoute();

  /**
   * Navigates forward to a new route by appending additional path segments to the current route.
   *
   * Caches the current route.
   *
   * @param path @type string|number - Additional path segments to be appended to the current route.
   */
  function forward(...path: (string|number)[]) {
    const _path = addPathSegments(route.path, ...path);
    router.navigate(_path, 'forward', 'push');
  }

  /**
   * Navigates "sideways" i.e neither forwards or backwards. Usually used to navigate between tabs.
   *
   * Caches the current route.
   *
   * @param path - The new path to navigate to.
   * @param replaceEntirePath - Optional. If true, replaces the entire path with the new path.
   *                            If false, replaces the last path segment with the new path.
   */
  function sideways(path: string, replaceEntirePath = false) {
    const _path = replaceEntirePath ? path : replaceLastPathSegment(route.path, path);

    router.navigate(_path, 'none', 'push');
  }

  /**
   * Navigates back in the router history.
   *
   * Does not cache the current route.
   *
   * @param path - The path to navigate back to.
   * @param count - The number of path segments to remove from the current path. Defaults to 1.
   * @param replaceEntirePath - Optional. If true, replaces the entire path with the new path.
   *                            If false, removes specified number of path segments from the new path.
   */
  function back(path: string, count = 1, replaceEntirePath = false) {
    const _path = replaceEntirePath ? path : removePathSegments(route.path, count);

    if (_path === '/') {
      return router.navigate('/home', 'root', 'replace');
    }

    router.navigate(_path, 'back', 'pop');
  }

  /**
   * Replaces the current path with the specified path.
   *
   * Does not cache the current route.
   *
   * @param path - The path to replace the current path with.
   * @param replaceEntirePath - Optional. If true, replaces the entire path with the new path.
   *                            If false, replaces the last path segment with the new path.
   */
  function replace(path: string, replaceEntirePath = false) {
    const _path = replaceEntirePath ? path : replaceLastPathSegment(route.path, path);

    router.navigate(_path, 'root', 'replace');
  }

  return { forward, sideways, back, replace, route };
}
